import React, { useEffect, useMemo } from 'react';
import {
  FieldValues,
  Path,
  PathValue,
  useController,
  useFormContext,
} from 'react-hook-form';
import {
  CustomFieldProps,
  QuestionnaireField,
} from 'components/stepper/field-components/QuestionnaireField';

import { findOtherFieldDependency } from 'components/stepper/field-components/QuestionnaireField';
import { DynamicQuestionnaireDependencie } from '../../dynamicQuestionnaire/interfaces/dynamicQuestionnaireResponse.interface';
import { Question } from '../../questionnaire/hooks/useQuestionnaire';
import { twMerge } from 'tailwind-merge';
import { ArrayQuestionnaireField } from '../../questionnaire/components/ArrayQuestionnaireField';

type DynamicDeps = {
  requiredFields: string[];
  disabledFields: string[];
};

type QuestionsById = Record<string, Question>;

type NestedDependentsRendererProps = {
  parentQuestion: Question;
  dependencies: DynamicQuestionnaireDependencie[];
  questionsById: QuestionsById;
  dynamicDeps?: DynamicDeps;
  renderedIds?: Set<string>;
  indentClassName?: string;
  customFields?: Partial<Record<string, React.ComponentType<CustomFieldProps>>>;
  hiddenFieldsIds?: string[];
};

function coerceToArray(val: unknown): string[] {
  if (Array.isArray(val)) return val.map(String);
  if (val == null) return [];
  if (typeof val === 'boolean') return [val ? 'true' : 'false'];
  return [String(val)];
}

function matchesTrigger(
  parentValue: unknown,
  triggerValues?: string[],
): boolean {
  if (!triggerValues?.length) return false;
  const set = new Set(coerceToArray(parentValue));
  return triggerValues.some((tv) => set.has(String(tv)));
}

export const NestedDependentsRenderer = <T extends FieldValues>({
  parentQuestion,
  dependencies,
  questionsById,
  dynamicDeps,
  renderedIds,
  indentClassName = 'ml-10',
  customFields,
  hiddenFieldsIds,
}: NestedDependentsRendererProps) => {
  const parentIdStr = String(parentQuestion.id) as Path<T>;
  const { control, setValue } = useFormContext<T>();
  const { field: parentField } = useController<T>({
    name: parentIdStr,
    control,
  });

  const compositeInfo = useMemo(
    () => findOtherFieldDependency(parentQuestion, dependencies ?? []),
    [parentQuestion, dependencies],
  );

  const dependentItems = useMemo(() => {
    return compositeInfo
      .map(({ dependentFieldId, triggerValues }) => {
        const q = questionsById[dependentFieldId];
        if (!q) return null;
        const show = matchesTrigger(parentField.value, triggerValues);
        return { q, triggerValues, show };
      })
      .filter(Boolean) as {
      q: Question;
      triggerValues: string[];
      show: boolean;
    }[];
  }, [compositeInfo, parentField.value, questionsById]);

  useEffect(() => {
    if (!renderedIds) return;
    dependentItems.forEach(({ q, show }) => {
      if (show) renderedIds.add(String(q.id));
    });
  }, [dependentItems, renderedIds]);

  useEffect(() => {
    dependentItems.forEach(({ q, show }) => {
      const idStr = String(q.id) as Path<T>;
      if (!show) {
        setValue(idStr, '' as PathValue<T, Path<T>>, { shouldValidate: true });
      }
    });
  }, [dependentItems, setValue]);

  if (!dependentItems.length) return null;

  return (
    <div
      className={twMerge(
        'flex flex-col desktop:flex-row w-auto gap-2.5',
        indentClassName,
      )}
    >
      {dependentItems.map(({ q, show }) => {
        const idStr = String(q.id) as Path<T>;
        const isRequired = !!dynamicDeps?.requiredFields?.includes(idStr);

        const CustomFieldCmp = customFields?.[idStr];
        if (hiddenFieldsIds?.includes(idStr)) return null;

        if (['ARRAY_FIELD'].includes(q.ui_control)) {
          if (renderedIds) {
            renderedIds.add(idStr);
          }

          return (
            <div
              className={twMerge(
                !show && 'opacity-50 pointer-events-none',
                'w-full',
              )}
            >
              <ArrayQuestionnaireField
                question={q}
                dependencies={dependencies}
                dynamicDeps={dynamicDeps as DynamicDeps}
                questionsById={questionsById}
                customFields={customFields}
                disabled={!show}
              />
            </div>
          );
        }

        return (
          <div
            key={q.id}
            className={twMerge(
              !show && 'opacity-50 pointer-events-none',
              'w-full',
            )}
          >
            {CustomFieldCmp ? (
              <CustomFieldCmp
                key={idStr}
                question={q}
                dependencies={dependencies}
                isRequired={isRequired}
                isDisabled={!show}
                questionsById={questionsById}
              />
            ) : (
              <QuestionnaireField<T>
                ui={q.ui_control}
                name={idStr}
                label={q.text || ''}
                answers={q.answers ?? []}
                required={isRequired}
                subtitle={q.subtitle ?? undefined}
                placeholder={q.helper_text ?? undefined}
                disabled={!show}
              />
            )}

            <NestedDependentsRenderer<T>
              parentQuestion={q}
              dependencies={dependencies}
              questionsById={questionsById}
              dynamicDeps={dynamicDeps}
              renderedIds={renderedIds}
              hiddenFieldsIds={hiddenFieldsIds}
              indentClassName={indentClassName}
            />
          </div>
        );
      })}
    </div>
  );
};
